import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BRANDS } from "@/lib/brands";
import { Recycle, LogOut, Filter, Mail, Phone, Calendar, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "qcm_admin_key";

const LoginView = ({ onLogin }) => {
  const [key, setKey] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await axios.post(`${API}/admin/verify`, null, {
        headers: { "X-Admin-Key": key },
      });
      localStorage.setItem(KEY_STORAGE, key);
      onLogin(key);
    } catch {
      setErr("Invalid admin key.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[color:var(--bg-dark)] text-white grid place-items-center px-6 relative isolate"
      data-testid="admin-login"
    >
      <div className="absolute inset-0 grid-lines-dark opacity-60 pointer-events-none" />
      <div className="absolute inset-0 noise pointer-events-none" />
      <form
        onSubmit={submit}
        className="relative border border-white/20 p-10 w-full max-w-md bg-black/40 backdrop-blur-xl"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-white text-black grid place-items-center">
            <Recycle size={18} strokeWidth={2.5} />
          </div>
          <div>
            <div className="eyebrow text-white/50">Admin Portal</div>
            <div className="font-display uppercase text-xl">QCM · Dale's</div>
          </div>
        </div>
        <label className="block mb-6">
          <span className="eyebrow text-white/60 mb-2 block">Admin Key</span>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="w-full border border-white/30 bg-black/50 px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-white transition-colors"
            placeholder="Enter admin key"
            data-testid="admin-key-input"
          />
        </label>
        {err && (
          <div className="mb-6 p-3 border border-red-500/40 bg-red-950/40 text-red-300 text-xs font-mono" data-testid="admin-login-error">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="hard-btn-inverse w-full justify-center disabled:opacity-60"
          data-testid="admin-login-btn"
        >
          {loading ? "Verifying…" : "Enter Dashboard"}
        </button>
        <Link
          to="/"
          className="mt-6 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white"
          data-testid="back-to-site"
        >
          <ArrowLeft size={12} /> Back to Site
        </Link>
      </form>
    </div>
  );
};

const DashboardView = ({ adminKey, onLogout }) => {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr("");
      try {
        const params = filter === "all" ? {} : { brand: filter };
        const { data } = await axios.get(`${API}/admin/contacts`, {
          headers: { "X-Admin-Key": adminKey },
          params,
        });
        setRows(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load submissions");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filter, adminKey]);

  const counts = {
    all: rows.length,
    qcm: rows.filter((r) => r.brand === "qcm").length,
    dales: rows.filter((r) => r.brand === "dales").length,
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-alt)]" data-testid="admin-dashboard">
      <div className="bg-[color:var(--bg-dark)] text-white">
        <div className="container-x flex items-center justify-between py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white text-black grid place-items-center">
              <Recycle size={18} strokeWidth={2.5} />
            </div>
            <div>
              <div className="eyebrow text-white/50">Admin Dashboard</div>
              <div className="font-display uppercase text-lg">
                QCM · Dale's Control
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hard-btn-inverse" data-testid="view-site-btn">
              View Site
            </Link>
            <button onClick={onLogout} className="hard-btn-inverse" data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="container-x py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <button
            onClick={() => setFilter("all")}
            className={`p-6 text-left border transition-colors ${
              filter === "all"
                ? "border-black bg-white"
                : "border-black/15 bg-white/50 hover:bg-white"
            }`}
            data-testid="filter-all"
          >
            <div className="eyebrow text-black/50 mb-2">Total Submissions</div>
            <div className="font-display text-5xl">{counts.all}</div>
          </button>
          <button
            onClick={() => setFilter("qcm")}
            className={`p-6 text-left border transition-colors ${
              filter === "qcm"
                ? "border-black bg-white"
                : "border-black/15 bg-white/50 hover:bg-white"
            }`}
            data-testid="filter-qcm"
          >
            <div className="eyebrow text-[color:var(--primary)] mb-2">
              Queen City Metal
            </div>
            <div className="font-display text-5xl">{counts.qcm}</div>
          </button>
          <button
            onClick={() => setFilter("dales")}
            className={`p-6 text-left border transition-colors ${
              filter === "dales"
                ? "border-black bg-white"
                : "border-black/15 bg-white/50 hover:bg-white"
            }`}
            data-testid="filter-dales"
          >
            <div className="eyebrow text-[color:var(--accent)] mb-2">
              Dale's Recycling
            </div>
            <div className="font-display text-5xl">{counts.dales}</div>
          </button>
        </div>

        <div className="flex items-center gap-3 mb-5">
          <Filter size={14} />
          <span className="eyebrow">
            Showing: {filter === "all" ? "Both Yards" : BRANDS[filter].short_name}
          </span>
        </div>

        <div
          className="border border-black/15 bg-white"
          data-testid="admin-submissions-list"
        >
          {loading && (
            <div className="p-10 text-center text-black/50 font-mono text-sm">
              Loading submissions…
            </div>
          )}
          {err && (
            <div className="p-10 text-center text-red-600 font-mono text-sm" data-testid="admin-error">
              {err}
            </div>
          )}
          {!loading && !err && rows.length === 0 && (
            <div className="p-10 text-center text-black/50 font-mono text-sm" data-testid="admin-empty">
              No submissions yet.
            </div>
          )}
          {!loading &&
            !err &&
            rows.map((r) => (
              <div
                key={r.id}
                className="p-6 border-t border-black/10 first:border-t-0 grid grid-cols-1 md:grid-cols-12 gap-4"
                data-testid={`submission-${r.id}`}
              >
                <div className="md:col-span-3">
                  <div className="eyebrow text-black/50 mb-1">From</div>
                  <div className="font-display uppercase">{r.name}</div>
                  <div
                    className={`inline-block mt-2 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${
                      r.brand === "qcm"
                        ? "bg-[color:var(--primary)] text-white"
                        : "bg-[color:var(--accent)] text-white"
                    }`}
                  >
                    {BRANDS[r.brand]?.short_name || r.brand}
                  </div>
                </div>
                <div className="md:col-span-4 text-sm space-y-2">
                  <div className="flex items-center gap-2 text-black/70">
                    <Mail size={12} /> <a href={`mailto:${r.email}`}>{r.email}</a>
                  </div>
                  {r.phone && (
                    <div className="flex items-center gap-2 text-black/70">
                      <Phone size={12} /> <a href={`tel:${r.phone}`}>{r.phone}</a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-black/50">
                    <Calendar size={12} />
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="md:col-span-5 text-sm text-black/80 whitespace-pre-wrap border-l md:pl-4 border-black/10">
                  {r.message}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const [adminKey, setAdminKey] = useState(localStorage.getItem(KEY_STORAGE));

  useEffect(() => {
    document.title = "Admin · QCM & Dale's";
  }, []);

  if (!adminKey) return <LoginView onLogin={setAdminKey} />;
  return (
    <DashboardView
      adminKey={adminKey}
      onLogout={() => {
        localStorage.removeItem(KEY_STORAGE);
        setAdminKey(null);
      }}
    />
  );
};

export default AdminPage;
